import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Save, AlertTriangle, MessageSquare } from 'lucide-react';
import { api, inputCls, Section, Field, Button, Notice } from './ui';
import { TRAFFIC_LABEL } from './Deals';
import SetupGuide from './SetupGuide';
import { SignatureFields, SignaturePreview, contactIssue, CONTACT_FIELDS, logosOf, logosIssue, cleanLogos } from './signature';

/**
 * Create or edit a team: the brand it works for, the saved deals it may offer,
 * the mailboxes the emails come from, and the pace they keep. Verticals and
 * markets come from the brand as MARS holds it; licences and currency from the
 * deals, where the deal desk asks for them.
 */

export const EMPTY = {
  name: '',
  brand_id: '',
  brand_pitch: '',
  program_name: '',
  program_signup_url: '',
  offer_ids: [],
  senders: [],
  sign_off: 'Best regards,',
  followup_days: [2, 3],
  reply_delay_min_minutes: 30,
  reply_delay_max_minutes: 120,
  outreach_delay_min_minutes: 1,
  outreach_delay_max_minutes: 15,
  send_window_start: 9,
  send_window_end: 18,
  timezone: 'Europe/London',
  weekdays_only: true,
  daily_new_contacts: 15,
  auto_scout: true,
  scout_batch_size: 20,
  min_organic_traffic: 500,
  licence_preference: 'any',
  skip_already_approached: true,
  use_semrush: true,
  web_research: true,
  approval_mode: 'review_deals',
  max_auto_replies: 8,
  // Listing fees (fees.js on the server): null is "as the deal says".
  listing_fee_ok: null,
  listing_fee_min: '',
  listing_fee_max: '',
  listing_fee_firmness: 'medium',
  // The Stop agent: days it steps back from a deal stalled on terms (0 is off), and check-ins before it lets go.
  cooloff_days: 2,
  cooloff_checkins: 1,
  // So the agents need not guess: who suits the brand, what is never said, which sites are never contacted.
  ideal_affiliates: '',
  brand_rules: '',
  exclude_domains: [],
  // What affiliates ask about the brand, what we want from them, and countries we take no players from.
  brand_facts: {},
  placement_goal: '',
  restricted_geos: []
};

// The brand facts the team chat asks, in its order (agent_teams.brand_facts).
export const BRAND_FACT_FIELDS = [
  { key: 'payments', label: 'Payment methods for players', placeholder: 'Visa, Mastercard, PayPal, Trustly, Apple Pay' },
  { key: 'withdrawals', label: 'Withdrawals and KYC', placeholder: 'E-wallets within 24 hours, cards 1 to 3 days; KYC at the first withdrawal' },
  { key: 'games', label: 'Games and betting', placeholder: '3,000+ slots from Pragmatic Play and NetEnt, Evolution live casino' },
  { key: 'app_support', label: 'Mobile app and player support', placeholder: 'iOS and Android apps, 24/7 live chat in English' },
  { key: 'creatives', label: 'Marketing material for affiliates', placeholder: 'Banners, logos and landing pages in the partner portal' }
];

// The never-contact list as the textarea shows it, one site per line; the server cleans what is typed.
export const domainsText = (v) => (Array.isArray(v) ? v.join('\n') : v || '');

export const FEE_FIRMNESS = [
  { key: 'low', label: 'Low', hint: 'Agree to their fee straight away, up to your most.' },
  { key: 'medium', label: 'Medium', hint: 'Bargain politely: open at your least, climb in steps, trade every step.' },
  { key: 'max', label: 'Max', hint: 'Bargain hard: performance first, then small steps, two holds before each.' }
];

export const TIMEZONES = ['Europe/London', 'Europe/Malta', 'Europe/Berlin', 'Europe/Stockholm', 'Europe/Madrid', 'Europe/Athens', 'Asia/Kolkata', 'Asia/Dubai', 'America/Toronto', 'America/New_York', 'America/Sao_Paulo', 'America/Mexico_City', 'Australia/Sydney', 'UTC'];

const blank = (v) => (v === null || v === undefined ? '' : v);

// Where each brand was added in MARS, in the order the pickers group them.
export const BRAND_KINDS = [
  { key: 'projects', label: 'Your brands (dashboard)' },
  { key: 'competitors', label: 'Competitor spy' },
  { key: 'gaps', label: 'Affiliate gap' }
];

const FactRow = ({ label, children, missing }) => (
  <div className="flex gap-3 py-0.5 text-sm">
    <span className="w-28 flex-none text-gray-500 dark:text-slate-400">{label}</span>
    {children
      ? <span className="text-gray-800 dark:text-slate-200">{children}</span>
      : <span className="text-amber-700 dark:text-amber-400">{missing}</span>}
  </div>
);

const fromTeam = (team) => ({
  ...EMPTY,
  ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, team[k] ?? EMPTY[k]])),
  offer_ids: team.offer_ids || [],
  senders: (team.senders || []).map((s) => ({
    mailbox_id: s.mailbox_id,
    email: s.email || null,
    name: blank(s.name || team.sender_name),
    title: blank(s.title || team.sender_title),
    teams: blank(s.teams),
    telegram: blank(s.telegram),
    linkedin: blank(s.linkedin),
    logos: logosOf(s).map((l) => ({ src: blank(l.src), href: blank(l.href) }))
  }))
});

const NumberInput = ({ value, onChange, suffix, min = 0, step = 1 }) => (
  <div className="flex items-center gap-1">
    <input type="number" min={min} step={step} className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} />
    {suffix && <span className="text-xs text-gray-500 dark:text-slate-400">{suffix}</span>}
  </div>
);

const TeamSetup = ({ meta, team, onSaved, onDeleted, onOpenDeals }) => {
  const [form, setForm] = useState(team ? fromTeam(team) : EMPTY);
  const [brands, setBrands] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [deals, setDeals] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setForm(team ? fromTeam(team) : EMPTY); setError(null); setSaved(false); }, [team]);

  useEffect(() => {
    api('/brands').then((d) => setBrands(d.brands || [])).catch(() => {});
    api('/mailboxes').then((d) => setMailboxes(d.mailboxes || [])).catch(() => {});
    api('/offers?status=active').then((d) => setDeals(d.offers || [])).catch(() => {});
  }, []);

  const set = (key, value) => { setSaved(false); setForm((f) => ({ ...f, [key]: value })); };
  const setSender = (i, key, value) => set('senders', form.senders.map((s, j) => (j === i ? { ...s, [key]: value } : s)));
  const addSender = (mailboxId) => {
    const box = mailboxes.find((m) => String(m.id) === String(mailboxId));
    if (!box || form.senders.some((s) => String(s.mailbox_id) === String(box.id))) return;
    const first = form.senders[0];
    // The links belong to the person, the logos usually to the brand: a new mailbox starts with the first one's logos.
    set('senders', [...form.senders, { mailbox_id: box.id, name: box.display_name || '', title: first?.title || 'Affiliate Manager', teams: '', telegram: '', linkedin: '', logos: (first?.logos || []).map((l) => ({ ...l })) }]);
  };

  const pickBrand = (id) => {
    const b = brands.find((x) => String(x.id) === String(id));
    setSaved(false);
    setForm((f) => ({ ...f, brand_id: id, name: f.name || (b ? `${b.brand_name} team` : '') }));
  };

  const followups = useMemo(() => form.followup_days || [], [form.followup_days]);
  const dayNumbers = useMemo(() => {
    let total = 0;
    return followups.map((d) => (total += Math.max(1, Number(d) || 1)));
  }, [followups]);

  const brand = brands.find((b) => String(b.id) === String(form.brand_id));
  const brandName = brand?.brand_name || team?.brand_name;
  const marketName = (code) => (meta.markets || []).find((m) => m.code === code)?.name || code;
  // What the team will take from its deals, as currently chosen (default deal first).
  const chosenDeals = form.offer_ids.map((id) => deals.find((d) => d.id === id)).filter(Boolean);
  const dealLicences = [...new Set(chosenDeals.flatMap((d) => d.licences || []))].sort();
  const dealCurrency = chosenDeals.find((d) => d.currency)?.currency;
  const legacyPolicy = !!team && !(team.offer_ids || []).length && (team.deal_policy?.allowed || []).length > 0;
  const licences = chosenDeals.length ? dealLicences : legacyPolicy ? team.licences || [] : [];
  const currency = chosenDeals.length ? dealCurrency : legacyPolicy ? team.currency : null;
  // The signature's lines as the server writes them (text.signatureLines); the links and logos go under them.
  const signatureFor = (s) => [form.sign_off || 'Best regards,', s?.name, [s?.title, brandName].filter(Boolean).join(' | ')].filter(Boolean);
  const unusedMailboxes = mailboxes.filter((m) => !form.senders.some((s) => String(s.mailbox_id) === String(m.id)));
  // Replies only come back if a mailbox this team actually sends from has IMAP.
  // Another readable mailbox sitting unused in Email Settings does not count.
  const repliesReadable = form.senders.some((s) => mailboxes.find((m) => String(m.id) === String(s.mailbox_id))?.can_read);

  const save = async () => {
    // A link or logo that would not work: said here, by field, before the server refuses it.
    for (const s of form.senders) {
      const issue = CONTACT_FIELDS.map((f) => contactIssue(f.key, s[f.key])).find(Boolean) || logosIssue(s.logos);
      if (issue) {
        const box = mailboxes.find((m) => String(m.id) === String(s.mailbox_id));
        setError(`${box?.email || s.email || 'A mailbox'}: ${issue}`);
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        ...form,
        senders: form.senders.map((s) => ({ ...s, logos: cleanLogos(s.logos), mailbox_id: Number(s.mailbox_id) })),
        followup_days: followups.map((d) => Number(d))
      };
      const data = team
        ? await api(`/teams/${team.id}`, { method: 'PUT', body })
        : await api('/teams', { method: 'POST', body });
      setSaved(true);
      onSaved(data.team);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!team) return;
    if (!window.confirm(`Delete "${team.name}"? Its affiliates, conversations and drafts are deleted too. Emails already sent stay sent.`)) return;
    try {
      await api(`/teams/${team.id}`, { method: 'DELETE' });
      onDeleted(team.id);
    } catch (err) {
      setError(err.message);
    }
  };

  const fit = team?.licence_fit || [];

  return (
    <div className="space-y-5">
      <SetupGuide
        team={team}
        mailboxes={mailboxes}
        readableMailbox={mailboxes.some((m) => m.can_read)}
        repliesReadable={repliesReadable}
        brandChosen={!!form.brand_id}
        dealsChosen={form.offer_ids.length > 0 || legacyPolicy}
        sendersChosen={form.senders.length > 0}
      />

      {team?.readiness?.length > 0 && (
        <Notice tone="warn">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
            <div>
              <div className="font-medium">Before this team can start:</div>
              <ul className="mt-1 list-disc pl-5">{team.readiness.map((r) => <li key={r}>{r}</li>)}</ul>
            </div>
          </div>
        </Notice>
      )}

      <Section tour="brand" title="Brand" hint="Everything here is what the agents are allowed to say about the brand. They will not invent anything beyond it.">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Team name">
            <input className={inputCls} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="BrandX UK" />
          </Field>
          <Field label="Brand" hint="Any brand you added in MARS: on the dashboard, in competitor spy or in affiliate gap.">
            <select className={inputCls} value={form.brand_id} onChange={(e) => pickBrand(e.target.value)}>
              <option value="">{brands.length ? 'Choose a brand' : 'No brands yet: add one in MARS first'}</option>
              {BRAND_KINDS.map((k) => {
                const list = brands.filter((b) => b.kind === k.key);
                return list.length > 0 && (
                  <optgroup key={k.key} label={k.label}>
                    {list.map((b) => <option key={b.id} value={b.id}>{b.brand_name} ({b.brand_url})</option>)}
                  </optgroup>
                );
              })}
            </select>
          </Field>
          {(brand || team) && (
            <div className="md:col-span-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-950">
              <div className="mb-1 text-xs text-gray-500 dark:text-slate-400">From the brand in MARS</div>
              <FactRow label="Verticals" missing={`None the agents work with${brand?.vertical_raw ? ` (the brand says "${brand.vertical_raw}")` : ''}. Set it on the brand.`}>
                {(brand ? brand.verticals : team.verticals || []).join(', ')}
              </FactRow>
              <FactRow label="Markets" missing="No countries on the brand. Add them on the brand.">
                {(brand ? brand.markets : team.target_geos || []).map(marketName).join(', ')}
              </FactRow>
              <div className="mb-1 mt-2 text-xs text-gray-500 dark:text-slate-400">From the deals this team offers (the deal desk asks for them)</div>
              <FactRow label="Licences" missing={chosenDeals.length ? 'Not given yet. Tell the deal desk in the Deals tab.' : 'Choose a deal below.'}>
                {licences.join(', ')}
              </FactRow>
              <FactRow label="Currency" missing={chosenDeals.length ? 'Not given yet. Tell the deal desk in the Deals tab.' : 'Choose a deal below.'}>
                {currency}
              </FactRow>
            </div>
          )}
          {fit.length > 0 && (
            <div className="md:col-span-2 space-y-1">
              {fit.filter((f) => !f.ok).map((f) => (
                <Notice key={f.geo} tone="warn">
                  {f.label}: the brand has no {f.needs.join('/') || 'licence'} for this market. Compliant affiliates there cannot list it. {f.note || ''}
                </Notice>
              ))}
            </div>
          )}
          <Field tour="pitch" label="Why an affiliate should list the brand" className="md:col-span-2" hint="Welcome offer, payment methods, game library, withdrawal speed, conversion, anything true and specific. The agents use only this.">
            <textarea rows={4} className={inputCls} value={form.brand_pitch || ''} onChange={(e) => set('brand_pitch', e.target.value)} />
          </Field>
          {BRAND_FACT_FIELDS.map((f) => (
            <Field key={f.key} label={f.label} hint={f.key === 'payments' ? 'What affiliates ask about the brand. Left empty, a colleague answers it.' : undefined}>
              <input className={inputCls} value={form.brand_facts?.[f.key] || ''} placeholder={f.placeholder}
                onChange={(e) => set('brand_facts', { ...(form.brand_facts || {}), [f.key]: e.target.value })} />
            </Field>
          ))}
          <Field label="What we want from an affiliate in return" hint="Where the brand should appear on their site.">
            <input className={inputCls} value={form.placement_goal || ''} onChange={(e) => set('placement_goal', e.target.value)} placeholder="A full review page and a top-3 spot in their lists" />
          </Field>
          <Field label="Never say or promise" className="md:col-span-2" hint="The brand's own rules for every email, e.g. never call a bonus free money, no word about crypto. The agents never break them.">
            <textarea rows={3} className={inputCls} value={form.brand_rules || ''} onChange={(e) => set('brand_rules', e.target.value)} placeholder="Never call a bonus risk-free or free money. Do not mention crypto payments." />
          </Field>
          <Field label="Affiliate programme name">
            <input className={inputCls} value={form.program_name || ''} onChange={(e) => set('program_name', e.target.value)} placeholder="BrandX Partners" />
          </Field>
          <Field label="Programme sign-up link" hint="Sent when a deal is agreed.">
            <input className={inputCls} value={form.program_signup_url || ''} onChange={(e) => set('program_signup_url', e.target.value)} placeholder="https://partners.brand.com/signup" />
          </Field>
        </div>
      </Section>

      <Section
        tour="deals"
        title="Deals this team offers"
        hint="Saved deals only: you explain a deal to the deal desk in the Deals tab, it asks what is missing, and you save it. Each affiliate gets the deal for its market (else the first one here) and keeps it for the whole conversation."
        actions={onOpenDeals && <Button icon={MessageSquare} onClick={onOpenDeals}>Create a deal by chat</Button>}
      >
        {legacyPolicy && (
          <div className="mb-3">
            <Notice tone="info">This team still uses the deal limits it was set up with. Choose a saved deal to replace them.</Notice>
          </div>
        )}
        {deals.length ? (
          <div className="grid gap-2 md:grid-cols-2">
            {deals.map((d) => {
              const on = form.offer_ids.includes(d.id);
              const position = form.offer_ids.indexOf(d.id);
              return (
                <button key={d.id} type="button"
                  onClick={() => set('offer_ids', on ? form.offer_ids.filter((x) => x !== d.id) : [...form.offer_ids, d.id])}
                  className={`rounded-lg border p-3 text-left transition-colors ${on
                    ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/30'
                    : 'border-gray-200 bg-white hover:border-gray-300 dark:border-slate-700 dark:bg-slate-900'}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-slate-100">{d.name}</span>
                    {on && position === 0 && <span className="rounded-full bg-blue-100 px-1.5 text-xs text-blue-700 dark:bg-blue-900/50 dark:text-blue-200">default</span>}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-slate-400">{d.opening_line || 'No opening offer yet'}</div>
                  <div className="text-xs text-gray-500 dark:text-slate-400">
                    {(d.geos || []).join(', ') || 'All markets'}{d.currency ? ` · ${d.currency}` : ''}
                    {(d.licences || []).length ? ` · ${d.licences.join(', ')}` : ' · no licences given'}
                    {d.traffic && d.traffic !== 'any' ? ` · ${TRAFFIC_LABEL[d.traffic]}` : ''}
                    {d.lead_with_offer ? ' · named in the first email' : ''}
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-slate-400">No saved deals yet. Create one by chat, then pick it here.</p>
        )}
      </Section>

      <Section tour="mailboxes" title="Mailboxes and signature" hint="The team sends from these mailboxes and reads replies from them. Each new affiliate gets the least busy one and keeps it for the whole thread.">
        {!mailboxes.length && <Notice tone="warn">Connect a mailbox in Email Settings first.</Notice>}
        <div className="space-y-3">
          {form.senders.map((s, i) => {
            const box = mailboxes.find((m) => String(m.id) === String(s.mailbox_id));
            return (
              <div key={s.mailbox_id} className="rounded-lg border border-gray-100 p-3 dark:border-slate-800">
                <div className="mb-2 flex items-center gap-2">
                  <span className="font-mono text-sm text-gray-800 dark:text-slate-200">{box?.email || s.email || `Mailbox ${s.mailbox_id}`}</span>
                  {i === 0 && <span className="rounded-full bg-gray-100 px-1.5 text-xs text-gray-600 dark:bg-slate-800 dark:text-slate-300">first</span>}
                  {box && !box.can_read && <span className="text-xs text-red-600 dark:text-red-400">No IMAP: replies cannot be read</span>}
                  {!box && mailboxes.length > 0 && <span className="text-xs text-red-600 dark:text-red-400">Removed in Email settings: remove it here and add the new mailbox</span>}
                  <button type="button" className="ml-auto text-gray-400 hover:text-red-600" title="Remove" onClick={() => set('senders', form.senders.filter((_, j) => j !== i))}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Sender name"><input className={inputCls} value={s.name} onChange={(e) => setSender(i, 'name', e.target.value)} placeholder="Sam Lee" /></Field>
                  <Field label="Designation"><input className={inputCls} value={s.title} onChange={(e) => setSender(i, 'title', e.target.value)} placeholder="Affiliate Manager" /></Field>
                  <SignatureFields sender={s} onChange={(key, value) => setSender(i, key, value)} />
                </div>
                {i > 0 && (
                  <div className="mt-3">
                    <div className="mb-1 text-xs font-medium text-gray-700 dark:text-slate-300">Preview</div>
                    <SignaturePreview lines={signatureFor(s)} sender={s} />
                  </div>
                )}
              </div>
            );
          })}
          {unusedMailboxes.length > 0 && (
            <div className="flex items-center gap-2">
              <select className={`${inputCls} max-w-sm`} value="" onChange={(e) => addSender(e.target.value)}>
                <option value="">{form.senders.length ? 'Add another mailbox' : 'Choose the mailbox to send from'}</option>
                {unusedMailboxes.map((m) => (
                  <option key={m.id} value={m.id} disabled={!m.can_read}>
                    {m.email}{m.is_verified ? '' : ' (not verified)'}{m.can_read ? '' : ' (no IMAP, cannot read replies)'}
                  </option>
                ))}
              </select>
              <Plus className="h-4 w-4 text-gray-400" />
            </div>
          )}
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <Field label="Sign-off"><input className={inputCls} value={form.sign_off || ''} onChange={(e) => set('sign_off', e.target.value)} placeholder="Best regards," /></Field>
          <div />
          <div className="md:col-span-2">
            <div className="mb-1 text-xs font-medium text-gray-700 dark:text-slate-300">Preview{form.senders[0] ? ` (${mailboxes.find((m) => String(m.id) === String(form.senders[0].mailbox_id))?.email || 'first mailbox'})` : ''}</div>
            <SignaturePreview lines={signatureFor(form.senders[0])} sender={form.senders[0]} />
          </div>
        </div>
      </Section>

      <Section tour="pace" title="Follow-ups and timing" hint="When an affiliate does not reply, the Contact agent follows up in the same thread. Each follow-up is counted from the email before it.">
        <div className="space-y-2">
          {followups.map((d, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="w-28 text-sm text-gray-700 dark:text-slate-300">Follow-up {i + 1}</span>
              <input type="number" min={1} max={30} className={`${inputCls} w-24`} value={d}
                onChange={(e) => set('followup_days', followups.map((x, j) => (j === i ? e.target.value : x)))} />
              <span className="text-sm text-gray-500 dark:text-slate-400">days after the previous email (day {dayNumbers[i]})</span>
              <button type="button" className="ml-auto text-gray-400 hover:text-red-600" onClick={() => set('followup_days', followups.filter((_, j) => j !== i))} title="Remove">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {followups.length < 6 && (
            <Button icon={Plus} onClick={() => set('followup_days', [...followups, 3])}>Add follow-up</Button>
          )}
          {followups.length === 0 && <p className="text-xs text-gray-500">No follow-ups: an affiliate who does not answer the first email is marked no reply.</p>}
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-3">
          <Field label="Reply to affiliates after (min, minutes)" hint="A random delay in this range, so answers do not look instant.">
            <NumberInput value={form.reply_delay_min_minutes} onChange={(v) => set('reply_delay_min_minutes', v)} />
          </Field>
          <Field label="... and at most (minutes)"><NumberInput value={form.reply_delay_max_minutes} onChange={(v) => set('reply_delay_max_minutes', v)} /></Field>
          <div className="hidden md:block" />
          <Field label="Send first emails and follow-ups after (min, minutes)" hint="Counted from when the email is written. 0 and 0 sends it straight away, inside the send window.">
            <NumberInput value={form.outreach_delay_min_minutes} onChange={(v) => set('outreach_delay_min_minutes', v)} />
          </Field>
          <Field label="... and at most (minutes)"><NumberInput value={form.outreach_delay_max_minutes} onChange={(v) => set('outreach_delay_max_minutes', v)} /></Field>
          <Field label="Time zone">
            <input list="agent-team-tz" className={inputCls} value={form.timezone} onChange={(e) => set('timezone', e.target.value)} />
            <datalist id="agent-team-tz">{TIMEZONES.map((tz) => <option key={tz} value={tz} />)}</datalist>
          </Field>
          <Field label="Send from (hour)"><NumberInput value={form.send_window_start} onChange={(v) => set('send_window_start', v)} /></Field>
          <Field label="Send until (hour)" hint="Same start and end means any time."><NumberInput value={form.send_window_end} onChange={(v) => set('send_window_end', v)} /></Field>
          <label className="mt-6 flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
            <input type="checkbox" checked={!!form.weekdays_only} onChange={(e) => set('weekdays_only', e.target.checked)} /> Weekdays only
          </label>
        </div>
      </Section>

      <Section title="Scouting" hint="How many new affiliates the team takes on, and what it looks for.">
        <label className="mb-4 flex items-start gap-2 text-sm text-gray-700 dark:text-slate-300">
          <input type="checkbox" className="mt-0.5" checked={form.auto_scout !== false} onChange={(e) => set('auto_scout', e.target.checked)} />
          <span>
            Search for new affiliates on its own (every 6 hours)
            <span className="block text-xs text-gray-500 dark:text-slate-400">Off: the team only emails the affiliates you hand over, and the ones you find with Scout now.</span>
          </span>
        </label>
        <div className="grid gap-4 md:grid-cols-3">
          <Field label="New affiliates emailed per day"><NumberInput value={form.daily_new_contacts} onChange={(v) => set('daily_new_contacts', v)} min={1} /></Field>
          <Field label="Scout picks per run"><NumberInput value={form.scout_batch_size} onChange={(v) => set('scout_batch_size', v)} min={5} /></Field>
          <Field label="Minimum organic traffic"><NumberInput value={form.min_organic_traffic} onChange={(v) => set('min_organic_traffic', v)} /></Field>
          <Field label="Affiliates that list">
            <select className={inputCls} value={form.licence_preference} onChange={(e) => set('licence_preference', e.target.value)}>
              <option value="any">Any brands</option>
              <option value="licensed">Nationally licensed brands</option>
              <option value="offshore">Offshore brands</option>
            </select>
          </Field>
          <label className="mt-6 flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
            <input type="checkbox" checked={!!form.skip_already_approached} onChange={(e) => set('skip_already_approached', e.target.checked)} /> Skip affiliates this account already emailed
          </label>
          <label className="mt-6 flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300" title="Affiliates are always judged on the GEO and keyword data already in MARS. This only looks up the brand's own traffic, once per brand, cached for a week.">
            <input type="checkbox" checked={!!form.use_semrush} onChange={(e) => set('use_semrush', e.target.checked)} /> Look up the brand's own traffic
          </label>
          <label className="mt-6 flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300" title="The Vetter may run a few web searches when something about a site is unclear (reputation, who runs it). Small extra cost per vet.">
            <input type="checkbox" checked={form.web_research !== false} onChange={(e) => set('web_research', e.target.checked)} /> Let the Vetter do a short web search
          </label>
          <Field label="Affiliates that suit the brand best" className="md:col-span-3" hint="The kind of site the Scout and Vetter should prefer, e.g. casino review sites, sports tipsters, comparison sites in Canada.">
            <textarea rows={2} className={inputCls} value={form.ideal_affiliates || ''} onChange={(e) => set('ideal_affiliates', e.target.value)} placeholder="Casino review and comparison sites with UK traffic" />
          </Field>
          <Field label="Countries we take no players from" className="md:col-span-3" hint="Two-letter codes, e.g. US, FR. The Scout skips sites whose visitors come mostly from there.">
            <input className={`${inputCls} font-mono`} value={Array.isArray(form.restricted_geos) ? form.restricted_geos.join(', ') : form.restricted_geos || ''}
              onChange={(e) => set('restricted_geos', e.target.value.toUpperCase())} placeholder="US, FR" />
          </Field>
          <Field label="Never contact these sites" className="md:col-span-3" hint="One per line: current partners, a rival's own sites. The Scout skips them, and they cannot be handed over.">
            <textarea rows={3} className={`${inputCls} font-mono`} value={domainsText(form.exclude_domains)} onChange={(e) => set('exclude_domains', e.target.value)} placeholder={'partner-site.com\nrival-owned-reviews.com'} />
          </Field>
        </div>
      </Section>

      <Section title="Listing fees" hint="Some sites charge a monthly fee for a spot on their page. The agents never bring one up; this is what they do when an affiliate asks.">
        <div className="grid gap-4 md:grid-cols-3">
          <Field label="If an affiliate asks for a listing fee">
            <select className={inputCls} value={form.listing_fee_ok == null ? '' : form.listing_fee_ok ? 'yes' : 'no'}
              onChange={(e) => set('listing_fee_ok', e.target.value === '' ? null : e.target.value === 'yes')}>
              <option value="">As the deal says</option>
              <option value="yes">We pay one if the site is worth it</option>
              <option value="no">No, performance deals only</option>
            </select>
          </Field>
          {form.listing_fee_ok === true && (
            <>
              <Field label="The least a month" hint="Where the agent opens. Empty: half the most.">
                <NumberInput value={form.listing_fee_min ?? ''} onChange={(v) => set('listing_fee_min', v)} suffix={currency || ''} />
              </Field>
              <Field label="The most a month" hint="Never more; past it a person decides.">
                <NumberInput value={form.listing_fee_max ?? ''} onChange={(v) => set('listing_fee_max', v)} suffix={currency || ''} />
              </Field>
            </>
          )}
        </div>
        {form.listing_fee_ok === true && (
          <div className="mt-4">
            <div className="mb-1 text-xs font-medium text-gray-700 dark:text-slate-300">How hard to bargain</div>
            <div className="grid gap-2 md:grid-cols-3">
              {FEE_FIRMNESS.map((f) => (
                <button key={f.key} type="button" onClick={() => set('listing_fee_firmness', f.key)}
                  className={`rounded-lg border p-3 text-left transition-colors ${form.listing_fee_firmness === f.key
                    ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/30'
                    : 'border-gray-200 bg-white hover:border-gray-300 dark:border-slate-700 dark:bg-slate-900'}`}>
                  <div className="font-medium text-gray-900 dark:text-slate-100">{f.label}</div>
                  <div className="text-xs text-gray-500 dark:text-slate-400">{f.hint}</div>
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
              Whatever you pick, Oddschecker, AskGamblers, Betfair and other big names (and any site MARS proves one of the biggest in your markets) get the soft approach: they earn their fee.
            </p>
          </div>
        )}
      </Section>

      <Section title="Stop agent" hint="When an affiliate will not meet us on terms, the deal is not closed straight away: the Stop agent steps back, then checks in once more in the same thread. You see every lead it holds in the On hold tab.">
        <div className="grid gap-4 md:grid-cols-3">
          <Field label="Step back for (days)" hint="0 turns it off: a no on terms closes the deal.">
            <NumberInput value={form.cooloff_days} onChange={(v) => set('cooloff_days', v)} min={0} />
          </Field>
          <Field label="Check-ins before it lets go">
            <select className={inputCls} value={form.cooloff_checkins} onChange={(e) => set('cooloff_checkins', Number(e.target.value))}>
              <option value={1}>Once</option>
              <option value={2}>Twice</option>
              <option value={3}>Three times</option>
            </select>
          </Field>
        </div>
      </Section>

      <Section tour="approvals" title="What needs your approval" hint="Whatever you choose, a draft that breaks a rule or goes over a deal limit always waits for a person.">
        <div className="grid gap-2 md:grid-cols-2">
          {(meta.approval_modes || []).map((m) => (
            <button key={m.key} type="button" onClick={() => set('approval_mode', m.key)}
              className={`rounded-lg border p-3 text-left transition-colors ${form.approval_mode === m.key
                ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/30'
                : 'border-gray-200 bg-white hover:border-gray-300 dark:border-slate-700 dark:bg-slate-900'}`}>
              <div className="font-medium text-gray-900 dark:text-slate-100">{m.label}</div>
              <div className="text-xs text-gray-500 dark:text-slate-400">{m.hint}</div>
            </button>
          ))}
        </div>
        <Field label="Automatic replies per conversation before a person checks" className="mt-4 max-w-xs">
          <NumberInput value={form.max_auto_replies} onChange={(v) => set('max_auto_replies', v)} min={1} />
        </Field>
      </Section>

      {error && <Notice tone="error">{error}</Notice>}
      {saved && <Notice tone="ok">Saved.</Notice>}
      <div data-tour="start" className="flex items-center gap-2">
        <Button variant="primary" icon={Save} busy={saving} onClick={save}>{team ? 'Save changes' : 'Create team'}</Button>
        {team && <Button variant="danger" icon={Trash2} onClick={remove} className="ml-auto">Delete team</Button>}
      </div>
    </div>
  );
};

export default TeamSetup;
